/**
 * Deterministic electrical instability for a damaged rig.
 *
 * The renderer samples this from simulation time, so replays and benchmark captures see
 * the same brownout at the same instant. Healthy lighting stays exactly at full power.
 */
export function rigDamageLightScale(integrity: number, time: number): number {
  const damage = clamp((75 - integrity) / 75, 0, 1);
  if (damage === 0) return 1;

  // Two incommensurate oscillations create brief, irregular-looking voltage drops
  // without randomness or wall-clock time.
  const signal =
    0.62 * Math.sin(time * 3.17 + 1.4)
    + 0.38 * Math.sin(time * 19.1 + 0.2);
  const threshold = 0.70 - damage * 0.45;
  const dropout = smoothstep(threshold, threshold + 0.22, signal);
  const flutter =
    damage * (0.015 + 0.025 * (0.5 + 0.5 * Math.sin(time * 8.7)));

  return clamp(
    1 - flutter - dropout * (0.20 + 0.65 * damage),
    0.18,
    1,
  );
}

/** Continuous visual damage levels, kept pure so saved low-integrity shifts restore exactly. */
export function rigDamageVisualLevels(
  integrity: number,
): { smoke: number; fire: number } {
  return {
    smoke: clamp((70 - integrity) / 40, 0, 1),
    fire: clamp((40 - integrity) / 30, 0, 1),
  };
}

function smoothstep(edge0: number, edge1: number, value: number): number {
  const t = clamp((value - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
